import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Box, Stack, Typography, Chip } from "@mui/material";
import { AccountBalanceWalletOutlined } from "@mui/icons-material";
import { getShippingPaymentDetail } from "api/financial";
import { MONO, money, fmtDate, ParcelsTable, PaymentLines, SlipLightbox, ChangeGivenBox, RelatedTransactionsBox, HeroMetaHeader, DetailDialogShell } from "./receiptParts";
import TransactionDetailDialog from "./TransactionDetailDialog";

// Full detail of a pending shipping-payment approval, shown as a modal so the
// approver can see which parcels are covered, each parcel's value (COD + shipping),
// the payment slips and the customer's wallet balance before deciding.
export default function ShippingApprovalDetailDialog({
  open,
  paymentId,
  onClose,
  onApprove,
  onReject,
  busy = false,
}) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // evidence filename shown full-screen
  const [txId, setTxId] = useState(null); // ledger transaction shown in a nested dialog

  useEffect(() => {
    if (!open || !paymentId) return;
    setLoading(true);
    setD(null);
    getShippingPaymentDetail(paymentId)
      .then((res) => setD(res && res.success ? res.data : null))
      .finally(() => setLoading(false));
  }, [open, paymentId]);

  const p = d?.payment;
  const parcels = d?.parcels || [];
  // Older records predate wallet-currency selection and only carry walletPortionTHB.
  const walletCurrency = p?.walletCurrency || "THB";
  const walletPortionAmount = p ? (p.walletPortionAmount || p.walletPortionTHB) : 0;
  // Live current balance — correct as "before" while still pending_approval (used
  // for the insufficient-funds check below, which must reflect what's actually in
  // the wallet right now), but not reliable as "after" once completed since later
  // wallet activity could've moved it. balanceBefore/After use the wallet debit's
  // own immutable ledger figures instead, whenever they're available (see backend
  // getShippingPaymentDetail).
  const balance = d?.walletBalance ?? d?.walletBalanceTHB;
  const walletShort =
    p && walletPortionAmount > 0 && balance != null && balance < walletPortionAmount;
  // Only meaningful once the debit actually happened — a still-pending or
  // rejected payment has no "after" state distinct from the current balance.
  const walletBalanceBefore = d?.walletTxnBalanceBefore ?? balance;
  const walletBalanceAfter = d?.walletTxnBalanceAfter ?? null;
  const showWalletBeforeAfter = p?.status === "completed" && walletBalanceAfter != null;
  // No cash/bank line at all — the whole fee was auto-charged to the wallet.
  const fullyPaidByWallet = p && (p.lines || []).length === 0 && walletPortionAmount > 0;

  // Label each transactionRef by reconstructing what backend's settle() actually
  // wrote, leg by leg, in the exact same order (controllers/financial.js): every
  // payment line, then the wallet-shortfall debit (if any), then every change
  // line — so "view transaction" rows read like a receipt instead of anonymous
  // numbered buttons.
  const transactionRows = p
    ? (() => {
        const legs = [];
        for (const line of p.lines || []) {
          legs.push({
            label: line.method === "bank_transfer" ? t("pickup.payment.bankTransfer") : t("pickup.payment.cash"),
            amount: line.amount,
            currency: line.currency,
            sign: "+",
          });
        }
        if (walletPortionAmount > 0) {
          legs.push({ label: t("financial.walletPortion"), amount: walletPortionAmount, currency: walletCurrency, sign: "-" });
        }
        for (const line of p.changeLines || []) {
          // A wallet-credit change line deposits into the customer's wallet ("+");
          // a cash change line withdraws physical cash from this station's drawer
          // to hand back to the customer ("-") — opposite directions of money flow.
          legs.push({
            label: line.source === "wallet_credit" ? t("pickup.payment.changeWallet") : t("pickup.payment.changeCash"),
            amount: line.amount,
            currency: line.currency,
            sign: line.source === "wallet_credit" ? "+" : "-",
          });
        }
        return (p.transactionRefs || []).map((id, i) => ({ id, ...legs[i] }));
      })()
    : [];

  return (
    <>
      <DetailDialogShell
        t={t}
        open={open}
        onClose={onClose}
        title={t("financial.requestDetail")}
        subtitle={p?.receiptId}
        loading={loading}
        empty={!p}
        actions={
          p && p.status === "pending_approval" && onApprove && onReject ? (
            <>
              <Button variant="outlined" color="error" disabled={busy} onClick={() => onReject(paymentId)}>
                {t("financial.reject")}
              </Button>
              <Button variant="contained" color="secondary" disabled={busy} onClick={() => onApprove(paymentId)}>
                {t("financial.approve")}
              </Button>
            </>
          ) : null
        }
      >
        {p && (
          <>
            <HeroMetaHeader
              heroLabel={t("financial.feeDue")}
              heroValue={money(p.feeTHB)}
              heroExtra={
                p.approvalReason ? (
                  <Chip size="small" color="warning" sx={{ mt: 0.5 }} label={t(`financial.reason.${p.approvalReason}`)} />
                ) : undefined
              }
              metaRows={[
                { label: t("financial.customer"), value: d.customer ? `${d.customer.name}${d.customer.whatsapp ? ` · ${d.customer.whatsapp}` : ""}` : "—" },
                { label: t("financial.station"), value: p.stationId },
                { label: t("financial.collectedBy"), value: p.collectedBy?.name },
                { label: t("financial.date"), value: fmtDate(p.createdAt) },
              ]}
            />

            <ParcelsTable t={t} parcels={parcels} totalTHB={p.feeTHB} serviceFeeTHB={p.serviceFeeTHB} />

            {/* wallet — only shown when some (or all) of the fee actually came out of
                the customer's wallet; a plain cash/bank-only payment has nothing to
                say here (walletPortionAmount stays 0). */}
            {walletPortionAmount > 0 && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: walletShort ? "error.main" : "divider",
                bgcolor: walletShort ? "rgba(211,47,47,0.08)" : "background.default",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AccountBalanceWalletOutlined fontSize="small" color={walletShort ? "error" : "secondary"} />
                <Box>
                  <Typography sx={{ fontSize: 11.5 }} color="text.secondary">
                    {(fullyPaidByWallet ? t("financial.paidByWallet") : t("financial.walletPortion")) +
                      (walletCurrency !== "THB" ? ` (${walletCurrency})` : "")}
                  </Typography>
                  <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 16 }} color={walletShort ? "error.main" : "text.primary"}>
                    {money(walletPortionAmount, walletCurrency)}
                  </Typography>
                </Box>
              </Stack>
              {showWalletBeforeAfter ? (
                <Stack direction="row" spacing={2}>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontSize: 11.5 }} color="text.secondary">{t("financial.balanceBefore")}</Typography>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 16 }} color="text.primary">
                      {money(walletBalanceBefore, walletCurrency)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontSize: 11.5 }} color="text.secondary">{t("financial.balanceAfter")}</Typography>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 16 }} color="text.primary">
                      {money(walletBalanceAfter, walletCurrency)}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ fontSize: 11.5 }} color="text.secondary">{t("financial.walletBalance")}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 16 }} color={walletShort ? "error.main" : "text.primary"}>
                    {balance == null ? "—" : money(balance, walletCurrency)}
                  </Typography>
                  {walletShort && (
                    <Typography sx={{ fontSize: 10.5 }} color="error.main">{t("financial.insufficientWallet")}</Typography>
                  )}
                </Box>
              )}
            </Stack>
            )}

            <PaymentLines
              t={t}
              lines={p.lines}
              onEnlarge={setLightbox}
              emptyLabel={fullyPaidByWallet ? t("financial.fullyPaidByWallet") : undefined}
            />

            {/* Change given back to the customer for an overpayment */}
            <ChangeGivenBox
              t={t}
              lines={p.changeLines}
              labelFor={(l) => (l.source === "wallet_credit" ? t("pickup.payment.changeWallet") : t("pickup.payment.changeCash"))}
            />

            {/* History context: once decided, show who decided it + the resulting
                ledger leg(s) instead of the Approve/Reject actions. */}
            {p.status !== "pending_approval" && (
              <RelatedTransactionsBox
                t={t}
                ok={p.status === "completed"}
                statusLabel={t(`financial.${p.status === "completed" ? "completed" : "rejected"}`)}
                subLabel={
                  p.status === "completed" && p.approvedBy?.name
                    ? `${t("financial.approvedBy")}: ${p.approvedBy.name}${p.approvedBy.at ? ` · ${fmtDate(p.approvedBy.at)}` : ""}`
                    : p.status === "rejected" && p.rejectionReason
                    ? `${t("financial.rejectionReason")}: ${p.rejectionReason}`
                    : null
                }
                refs={transactionRows}
                onView={setTxId}
              />
            )}
          </>
        )}
      </DetailDialogShell>

      <TransactionDetailDialog open={!!txId} txId={txId} onClose={() => setTxId(null)} />

      <SlipLightbox filename={lightbox} onClose={() => setLightbox(null)} t={t} />
    </>
  );
}
